import { Button, Modal, TextInput } from '@octano/global-ui';
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { useValidations } from '../../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../../locales/translations';
import { isNumericText } from '../../../../../utils/text';
import { CourseFormFields, CourseLearningUnit } from '../../types';

interface Props {
  onConfirm: (data: any) => void;
  onDecline: () => void;
}

export interface ModalAddCourseLearningUnitRef {
  openModal: (row?: number) => void;
  closeModal: () => void;
}

type CourseLearningUnitTpe = CourseLearningUnit | undefined;

const ModalAddCourseLearningUnit = forwardRef<
  ModalAddCourseLearningUnitRef,
  Props
>(({ onConfirm, onDecline }, ref) => {
  const { t } = useTranslation(TranslationsKeys.ACADEMIC_OFFER_COURSES_UPDATE);

  const [modal, setModal] = useState(false);
  const [
    courseLearningUnit,
    setCourseLearningUnit,
  ] = useState<CourseLearningUnitTpe>(undefined);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const { msgValidations } = useValidations();
  const { watch, getValues, setValue } = useFormContext<CourseFormFields>();
  const learningUnits = watch('learningUnits');

  //cuando se edita un learning units, se agrega su index en la tabla
  const action = useMemo(
    () =>
      typeof courseLearningUnit?.indexOnTable === 'number' ? 'edit' : 'add',
    [courseLearningUnit?.indexOnTable],
  );

  //Valida si se pueden agregar nuevos learning units
  const canAddNewLearningUnit = useMemo(() => {
    if (action === 'edit' && courseLearningUnit) {
      return !learningUnits?.some(
        (lr, index) =>
          lr.title === courseLearningUnit.title &&
          index !== courseLearningUnit.indexOnTable,
      );
    }
    return !learningUnits?.some((lr) => lr.title === courseLearningUnit?.title);
  }, [learningUnits, courseLearningUnit, action]);

  const textError = useMemo(() => {
    if (!hasSubmitted) {
      return undefined;
    }

    const errors: Record<string, string> = {};

    if (!canAddNewLearningUnit) {
      errors.title =
        action === 'edit'
          ? t('learningUnit.modalAdd.error.titleDuplicate')
          : t('learningUnit.modalAdd.error.title');
    }
    if (!courseLearningUnit?.title) {
      errors.title = msgValidations.required;
    }
    if (!courseLearningUnit?.hours) {
      errors.hours = msgValidations.required;
    }
    if (
      courseLearningUnit?.hours &&
      !isNumericText(courseLearningUnit?.hours)
    ) {
      errors.hours = msgValidations.invalidDecimalNumeric;
    }

    return Object.keys(errors).length > 0 ? errors : undefined;
  }, [
    hasSubmitted,
    courseLearningUnit?.title,
    courseLearningUnit?.hours,
    msgValidations.required,
    msgValidations.invalidDecimalNumeric,
    action,
    canAddNewLearningUnit,
    t,
  ]);

  const onSave = useCallback(() => {
    if (action === 'add') {
      courseLearningUnit && onConfirm(courseLearningUnit);
      setCourseLearningUnit(undefined);
    } else {
      const index = courseLearningUnit!.indexOnTable!;
      setValue(`learningUnits.${index}`, {
        ...courseLearningUnit,
      });
    }
    setHasSubmitted(false);
    setModal(false);
  }, [onConfirm, action, courseLearningUnit, setValue]);

  const handleAccept = () => {
    setHasSubmitted(true);

    const hasErrors =
      !canAddNewLearningUnit ||
      !courseLearningUnit?.title ||
      !courseLearningUnit?.hours ||
      (courseLearningUnit?.hours && !isNumericText(courseLearningUnit.hours));

    if (hasErrors) {
      return;
    }
    onSave();
  };

  useImperativeHandle(
    ref,
    () => ({
      openModal(index?: number) {
        if (typeof index === 'number') {
          const learningUnit = getValues(`learningUnits.${index}`);
          learningUnit.indexOnTable = index;
          setCourseLearningUnit(learningUnit);
        }
        setHasSubmitted(false);
        setModal(true);
      },
      closeModal() {
        setHasSubmitted(false);
        setModal(false);
      },
    }),
    [getValues],
  );

  return (
    <Modal
      isOpen={modal}
      onClosed={() => {
        setCourseLearningUnit(undefined);
      }}
      toggle={() => !modal}
      size="lg"
      unmountOnClose
    >
      <Row>
        <Col xs={12}>
          <p className="fs-22 text-dark text-center mb-4">
            {action === 'add'
              ? t(`learningUnit.modalAdd.title`)
              : t(`learningUnit.modalAdd.editTitle`)}
          </p>
        </Col>
        <Col xs={12}>
          <TextInput
            name="title"
            label={t(`learningUnit.modalAdd.fields.title`)}
            value={courseLearningUnit?.title || ''}
            onChange={(e) => {
              let newValue = e.target.value;
              setCourseLearningUnit((prev) => ({
                ...prev,
                title: newValue,
              }));
            }}
            errorText={textError?.title}
          />
        </Col>
        <Col xs={12}>
          <TextInput
            name="hours"
            label={t(`learningUnit.modalAdd.fields.hours`)}
            value={courseLearningUnit?.hours}
            defaultValue={0}
            placeholder="0"
            onChange={(e) => {
              let newValue = e.target.value;
              setCourseLearningUnit((prev) => {
                return { ...prev, hours: newValue };
              });
            }}
            errorText={textError?.hours}
          />
        </Col>
      </Row>
      <Row className="pt-5">
        <Col xs={12} lg={6} className="pb-2 order-2 order-lg-1">
          <Button
            type="button"
            outlined
            onClick={() => {
              onDecline();
              setCourseLearningUnit(undefined);
              setModal(false);
              setHasSubmitted(false);
            }}
            text={t(`learningUnit.modalAdd.btn.cancel`)}
            fullwidth
          />
        </Col>
        <Col xs={12} lg={6} className="pb-2 order-1 order-lg-2">
          <Button
            text={
              action === 'add'
                ? t(`learningUnit.modalAdd.btn.save`)
                : t(`learningUnit.modalAdd.btn.edit`)
            }
            onClick={handleAccept}
            fullwidth
            disabled={action === 'edit' && !canAddNewLearningUnit}
          />
        </Col>
      </Row>
    </Modal>
  );
});

export default ModalAddCourseLearningUnit;
